/**
 * Standalone pages (legal and others), served at /<slug>. Bodies are Markdown.
 * `footer: true` lists the page in the footer's bottom bar. Edit in the dashboard → Pages.
 */
export const pages = [
  {
    slug: 'privacy-policy',
    footer: true,
    updated: '2026-10-06',
    title: { en: 'Privacy Policy', ar: 'سياسة الخصوصية' },
    description: {
      en: 'How B-Code collects, uses and protects the information you share with us.',
      ar: 'كيف تجمع B-Code المعلومات التي تشاركها معنا وتستخدمها وتحميها.',
    },
    body: {
      en: `This policy explains what information B-Code ("we", "us") collects through this website, how we use it, and the choices you have.

## Information we collect

**Information you send us.** When you use the contact form, we receive the details you enter: your name, email address, phone and WhatsApp numbers, the service you are interested in, and your message. If you contact us by WhatsApp, phone or email, we receive the information you share in that conversation.

**Information stored on your device.** The website saves a few preferences in your browser's local storage — your language, light or dark theme, and a cached copy of the site content so pages load faster. This data stays on your device and is not used to track you.

We do not use advertising trackers on this website, and we do not sell your information.

## How we use your information

- To reply to your enquiry and prepare proposals or quotes.
- To deliver and support services you have asked for.
- To keep a record of our communication with you.
- To keep the website secure and working properly.

## Sharing

We share information only with service providers that help us run the website and our business — for example hosting, security and email delivery — and only as needed for those services. We may also disclose information if required by law.

## How long we keep it

We keep enquiry and project records for as long as needed to serve you and to meet legal or accounting requirements, then delete or anonymize them.

## Security

We use reasonable technical and organizational measures to protect your information, including encrypted connections (HTTPS). No method of transmission or storage is completely secure, but we work to protect your data.

## Your choices

You can ask us to access, correct or delete the personal information we hold about you. You can also clear the preferences stored in your browser at any time from your browser settings.

## Changes to this policy

We may update this policy from time to time. The latest version is always available on this page.

## Contact us

For any privacy question or request, please [contact us](/#contact).`,
      ar: `توضح هذه السياسة المعلومات التي تجمعها B-Code ("نحن") عبر هذا الموقع، وكيف نستخدمها، والخيارات المتاحة لك.

## المعلومات التي نجمعها

**المعلومات التي ترسلها إلينا.** عند استخدام نموذج التواصل، نستلم البيانات التي تُدخلها: اسمك وبريدك الإلكتروني ورقم هاتفك ورقم واتساب والخدمة التي تهمك ورسالتك. وإذا تواصلت معنا عبر واتساب أو الهاتف أو البريد الإلكتروني، فإننا نستلم المعلومات التي تشاركها في تلك المحادثة.

**المعلومات المحفوظة على جهازك.** يحفظ الموقع بعض التفضيلات في التخزين المحلي لمتصفحك — اللغة، والوضع الفاتح أو الداكن، ونسخة مؤقتة من محتوى الموقع لتسريع تحميل الصفحات. تبقى هذه البيانات على جهازك ولا تُستخدم لتتبعك.

لا نستخدم أدوات تتبع إعلانية في هذا الموقع، ولا نبيع معلوماتك.

## كيف نستخدم معلوماتك

- للرد على استفسارك وإعداد العروض وعروض الأسعار.
- لتقديم الخدمات التي طلبتها ودعمها.
- للاحتفاظ بسجل تواصلنا معك.
- للحفاظ على أمان الموقع وعمله بشكل سليم.

## مشاركة المعلومات

نشارك المعلومات فقط مع مزوّدي الخدمات الذين يساعدوننا في تشغيل الموقع وأعمالنا — مثل الاستضافة والحماية وإرسال البريد الإلكتروني — وبالقدر اللازم لتلك الخدمات فقط. وقد نُفصح عن المعلومات إذا اقتضى القانون ذلك.

## مدة الاحتفاظ

نحتفظ بسجلات الاستفسارات والمشاريع طوال المدة اللازمة لخدمتك وللوفاء بالمتطلبات النظامية والمحاسبية، ثم نحذفها أو نجعلها مجهولة الهوية.

## الحماية

نتخذ إجراءات تقنية وتنظيمية مناسبة لحماية معلوماتك، بما في ذلك الاتصال المشفّر (HTTPS). لا توجد وسيلة نقل أو تخزين آمنة تماماً، لكننا نعمل على حماية بياناتك.

## خياراتك

يمكنك أن تطلب منا الاطلاع على معلوماتك الشخصية التي نحتفظ بها أو تصحيحها أو حذفها. كما يمكنك مسح التفضيلات المحفوظة في متصفحك في أي وقت من إعدادات المتصفح.

## التعديلات على هذه السياسة

قد نُحدّث هذه السياسة من وقت لآخر، وتتوفر أحدث نسخة منها دائماً في هذه الصفحة.

## تواصل معنا

لأي سؤال أو طلب يتعلق بالخصوصية، يُرجى [التواصل معنا](/#contact).`,
    },
  },
  {
    slug: 'terms',
    footer: true,
    updated: '2026-10-06',
    title: { en: 'Terms & Conditions', ar: 'الشروط والأحكام' },
    description: {
      en: 'The terms that apply when you use the B-Code website and request our services.',
      ar: 'الشروط التي تنطبق عند استخدامك لموقع B-Code وطلبك لخدماتنا.',
    },
    body: {
      en: `By using this website you agree to these terms. If you do not agree, please do not use the website.

## About these terms

These terms cover your use of the B-Code website. Projects and services we deliver are governed by the proposal, quote or contract we agree with you; where that agreement differs from these terms, the agreement applies.

## Use of the website

- Use the website lawfully and do not attempt to disrupt, damage or gain unauthorized access to it.
- Do not copy or reuse the website's design, text, images or code without our written permission.
- Information you submit must be accurate and must not infringe anyone else's rights.

## Prices and quotes

Packages and prices shown on the website are indicative and may change without notice. A price becomes binding only when it is confirmed in a written quote or contract.

## Portfolio and content

Projects shown in our portfolio are presented with our clients' consent or are publicly available. Client names, logos and trademarks belong to their respective owners. Articles on our blog are for general information and are not professional advice for your specific situation.

## Intellectual property

The B-Code name, logo, website design and content are owned by B-Code unless stated otherwise. Ownership of work we create for clients is defined in each client's contract.

## Third-party links

The website may link to other websites, including our clients' sites. We are not responsible for the content or practices of those websites.

## Limitation of liability

The website is provided "as is". To the extent permitted by law, B-Code is not liable for indirect or consequential losses arising from use of the website or reliance on its content.

## Changes

We may update these terms from time to time. Continued use of the website after changes means you accept the updated terms.

## Contact us

For any question about these terms, please [contact us](/#contact).`,
      ar: `باستخدامك لهذا الموقع فإنك توافق على هذه الشروط. إذا لم توافق عليها، يُرجى عدم استخدام الموقع.

## نطاق هذه الشروط

تنطبق هذه الشروط على استخدامك لموقع B-Code. أما المشاريع والخدمات التي نقدمها فتحكمها العروض أو عروض الأسعار أو العقود التي نتفق عليها معك، وفي حال اختلافها عن هذه الشروط يُعمل بما ورد في الاتفاق.

## استخدام الموقع

- استخدم الموقع بشكل نظامي، ولا تحاول تعطيله أو إلحاق الضرر به أو الوصول إليه دون إذن.
- لا تنسخ تصميم الموقع أو نصوصه أو صوره أو شيفرته ولا تُعِد استخدامها دون إذن كتابي منا.
- يجب أن تكون المعلومات التي ترسلها صحيحة وألا تنتهك حقوق أي طرف آخر.

## الأسعار والعروض

الباقات والأسعار المعروضة في الموقع استرشادية وقد تتغير دون إشعار مسبق، ولا يصبح السعر ملزماً إلا بعد تأكيده في عرض سعر أو عقد مكتوب.

## الأعمال والمحتوى

تُعرض المشاريع في معرض أعمالنا بموافقة عملائنا أو لكونها متاحة للعموم. أسماء العملاء وشعاراتهم وعلاماتهم التجارية مملوكة لأصحابها. والمقالات في مدونتنا للمعلومات العامة وليست استشارة مهنية لحالتك الخاصة.

## الملكية الفكرية

اسم B-Code وشعارها وتصميم الموقع ومحتواه مملوكة لـ B-Code ما لم يُذكر خلاف ذلك. أما ملكية الأعمال التي ننجزها للعملاء فتحددها عقودهم.

## روابط الأطراف الأخرى

قد يحتوي الموقع على روابط لمواقع أخرى، منها مواقع عملائنا، ولسنا مسؤولين عن محتوى تلك المواقع أو ممارساتها.

## حدود المسؤولية

يُقدَّم الموقع "كما هو". وبالقدر الذي يسمح به النظام، لا تتحمل B-Code المسؤولية عن الخسائر غير المباشرة أو التبعية الناتجة عن استخدام الموقع أو الاعتماد على محتواه.

## التعديلات

قد نُحدّث هذه الشروط من وقت لآخر، ويُعد استمرارك في استخدام الموقع بعد التعديل قبولاً للشروط المحدّثة.

## تواصل معنا

لأي سؤال حول هذه الشروط، يُرجى [التواصل معنا](/#contact).`,
    },
  },
];
